import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, ModalBody } from 'reactstrap';

import tickWhite from '../../../assets/img/icons/tick_2.svg';
import CustomCheckboxDropdown from '../../../components/CustomCheckboxDropdown/CustomCheckboxDropdown';
import CreateProjectDropdown from '../../../components/DropdownStyle/CreateProjectDropdown';
import ButtonTextLoader from '../../../components/Loaders/ButtonTextLoader';
import Select from '../../../components/ReactSelectDropdown';
import { filterOption } from '../../../components/ReactSelectDropdown/ReactSelectDropdown';
import { PrimaryButton, WhiteButton } from '../../../styledComponents/buttons';
import {
  CommonFlex,
  CommonGrid,
  CommonText,
  FormAlert,
  InputField,
  InputLabel,
  InputWithLabelSection,
  RedStar,
} from '../../../styledComponents/common';
import {
  CheckboxDiv,
  CheckboxTickIcon,
} from '../../../styledComponents/settings';
import { checkHtml, checkUrl } from '../../../utils/helper';

const AddMasterSubTaskModal = ({
  isOpen,
  toggle,
  publicZoneList,
  getOfficesShortList,
  officesShortList,
  addMasterSubTask,
  isLoading,
  reload,
  setReload,
  editMasterSubTask,
  taskToEdit,
  isEditMode,
  getMasterTasks,
  masterTasks,
  masterTasksLoading,
}) => {
  const { t } = useTranslation();

  const [taskName, setTaskName] = useState('');
  const [saveErrors, setSaveErrors] = useState({});
  const [selectedPublicZone, setSelectedPublicZone] = useState('');
  const [publicZoneOptions, setPublicZoneOptions] = useState([]);
  const [selectedOffices, setSelectedOffices] = useState([]);
  const [officeOptions, setOfficeOptions] = useState([]);
  const [selectedMasterTask, setSelectedMasterTask] = useState([]);
  const [masterTaskOptions, setMasterTaskOptions] = useState([]);
  const [priority, setPriority] = useState('high');
  const priorityOptions = [
    { label: t('high'), value: 'high' },
    { label: t('no_priority'), value: 'no' },
  ];
  const [checkOfficeReport, setCheckOfficeReport] = useState(false);

  // Add a state to track if "All Offices" is selected
  const [selectAllOffices, setSelectAllOffices] = useState(false);

  // Populate the form with taskToEdit data if in edit mode
  useEffect(() => {
    if (isEditMode && taskToEdit) {
      setTaskName(taskToEdit.name || '');
      setPriority(taskToEdit.priority || 'high');
      setCheckOfficeReport(taskToEdit.add_to_office_report || false);

      // Set master tasks if available
      if (
        taskToEdit.master_tasks &&
        taskToEdit.master_tasks.length > 0 &&
        masterTaskOptions.length > 0
      ) {
        const selectedMasterTasks = taskToEdit.master_tasks.map(task => ({
          label: task.name ? `${task.code} - ${task.name}` : task.code,
          value: task.id,
        }));
        setSelectedMasterTask(selectedMasterTasks);
      }

      // Set public zone if available
      if (taskToEdit.public_zone_id && publicZoneOptions.length > 0) {
        const publicZone = publicZoneOptions.find(
          zone => zone.value === taskToEdit.public_zone_id,
        );
        if (publicZone) {
          setSelectedPublicZone(publicZone);
        }
      }

      // Handle related offices
      if (
        taskToEdit.office &&
        taskToEdit.office.length > 0 &&
        officeOptions.length > 0
      ) {
        const relatedOffices = taskToEdit.office.map(office => ({
          label: office.name,
          value: office.id,
        }));
        setSelectedOffices(relatedOffices);
      }
    }
  }, [
    isEditMode,
    taskToEdit,
    // publicZoneOptions,
    // masterTaskOptions,
    // officeOptions,
  ]);

  useEffect(() => {
    if (publicZoneList && publicZoneList.length > 0) {
      let newArr = [];
      publicZoneList.forEach(zone => {
        newArr.push({
          label: zone.name,
          value: zone.id,
        });
      });
      setPublicZoneOptions(newArr);

      // Only set default if not in edit mode or no selected zone yet
      if (!isEditMode || !selectedPublicZone) {
        setSelectedPublicZone(newArr[0]);
      }
    }
  }, [publicZoneList]);

  useEffect(() => {
    if (selectedPublicZone?.value) {
      // Fetch master tasks based on public zone
      if (getMasterTasks) {
        getMasterTasks({
          public_zone_id: selectedPublicZone.value,
        });
      }

      if (getOfficesShortList) {
        getOfficesShortList({
          public_zone_id: selectedPublicZone.value,
        });
      }
    } else if (getOfficesShortList) {
      getOfficesShortList();
    }
  }, [selectedPublicZone]);

  // Update master task options when masterTasks changes
  useEffect(() => {
    if (masterTasks && masterTasks.length > 0) {
      const taskOptions = masterTasks.map(task => ({
        label: task.name,
        value: task.id,
      }));
      setMasterTaskOptions(taskOptions);
    } else {
      setMasterTaskOptions([]);
    }
  }, [masterTasks]);

  useEffect(() => {
    if (officesShortList && officesShortList.length > 0) {
      let list = [];
      officesShortList.forEach(office => {
        list.push({
          label: office.name,
          value: office.id,
        });
      });
      setOfficeOptions(list);

      // If in edit mode and we have related offices, check if all offices are selected
      if (isEditMode && taskToEdit && taskToEdit.office) {
        if (taskToEdit.office.length === officesShortList.length) {
          setSelectAllOffices(true);
          setSelectedOffices(list); // Select all offices
        } else {
          setSelectAllOffices(false);
          const relatedOffices = taskToEdit.office.map(office => ({
            label: office.name,
            value: office.id,
          }));
          setSelectedOffices(relatedOffices);
        }
      } else if (!isEditMode) {
        // Reset selected offices when office options change but only when not in edit mode
        setSelectedOffices([]);
        setSelectAllOffices(false);
      }
    }
  }, [officesShortList, isEditMode, taskToEdit]);

  const onPublicZoneChange = e => {
    setSelectedPublicZone(e);
    // Reset offices and master task when public zone changes
    setSelectedOffices([]);
    setSelectAllOffices(false);
    setSelectedMasterTask([]);
  };

  const onOfficeChange = selected => {
    // Check if "All Offices" is among the selected options
    const allOfficesOption = selected.find(option => option.value === 'all');

    if (allOfficesOption) {
      // If "All Offices" is selected, select all office options
      setSelectAllOffices(true);
      setSelectedOffices(officeOptions);
    } else {
      // Regular selection
      setSelectAllOffices(false);
      setSelectedOffices(selected);
    }

    if (saveErrors?.office) {
      setSaveErrors({ ...saveErrors, office: undefined });
    }
  };

  const onMasterTaskChange = selected => {
    setSelectedMasterTask(selected);
    if (saveErrors?.master_task) {
      setSaveErrors({ ...saveErrors, master_task: undefined });
    }
  };

  const checkError = () => {
    let errors = {};
    let formIsValid = true;

    if (!taskName.trim()) {
      formIsValid = false;
      errors['taskName'] = 'Task name is required';
    } else if (checkUrl(taskName)) {
      formIsValid = false;
      errors['taskName'] = 'Task name cannot contain url';
    } else if (checkHtml(taskName)) {
      formIsValid = false;
      errors['taskName'] = 'Invalid task name';
    }

    if (!selectedPublicZone) {
      formIsValid = false;
      errors['public_zone'] = 'Public Zone is required';
    }

    // Check if offices are selected - either through individual selection or "All Offices"
    if (
      !selectAllOffices &&
      (!selectedOffices || selectedOffices.length === 0)
    ) {
      formIsValid = false;
      errors['office'] = 'At least one office must be selected';
    }

    if (!selectedMasterTask || selectedMasterTask.length === 0) {
      formIsValid = false;
      errors['master_task'] = 'At least one master task must be selected';
    }

    if (!priority) {
      formIsValid = false;
      errors['priority'] = 'Priority is required';
    }

    setSaveErrors(errors);
    return formIsValid;
  };

  const handleSubmit = () => {
    if (checkError()) {
      // Prepare the office IDs for submission
      let officeIds = [];
      if (selectAllOffices) {
        // If "All Offices" is selected, include all office IDs
        officeIds = officeOptions.map(office => office.value);
      } else {
        // Otherwise, use the selected offices
        officeIds = selectedOffices.map(office => office.value);
      }

      const payload = {
        name: taskName,
        public_zone_id: selectedPublicZone.value,
        office_ids: officeIds,
        priority: priority,
        master_task_ids: selectedMasterTask.map(task => task.value),
        add_to_office_report: checkOfficeReport,
      };

      if (isEditMode && taskToEdit) {
        editMasterSubTask({
          task_id: taskToEdit.id,
          ...payload,
          callback: () => {
            handleToggle();
            if (setReload) setReload(prev => !prev);
          },
        });
      } else if (addMasterSubTask) {
        addMasterSubTask({
          ...payload,
          callback: () => {
            handleToggle();
            if (setReload) setReload(prev => !prev);
          },
        });
      }
    }
  };

  const clearState = () => {
    setTaskName('');
    setSaveErrors({});
    setSelectedPublicZone('');
    setSelectedOffices([]);
    setSelectedMasterTask([]);
    setPriority('high');
  };

  const handleToggle = () => {
    clearState();
    toggle();
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleToggle}
      style={{ marginTop: `2vh`, maxWidth: `600px` }}
    >
      <ModalBody style={{ padding: `20px` }}>
        <CommonText name title fontSize="16px" margin="auto 0 20px 0">
          {isEditMode ? t('edit_master_subtask') : t('add_master_subtask')}
        </CommonText>

        <InputWithLabelSection margin="20px 0 0">
          <InputLabel>
            {t('public_zone')} <RedStar>*</RedStar>
          </InputLabel>
          <Select
            isSearchable
            value={selectedPublicZone}
            options={publicZoneOptions}
            placeholder={t('select_zone')}
            onChange={onPublicZoneChange}
            styles={CreateProjectDropdown()}
            filterOption={filterOption}
          />
          {saveErrors?.public_zone && (
            <FormAlert margin="0 0 0 5px">{saveErrors.public_zone}</FormAlert>
          )}
        </InputWithLabelSection>

        <InputWithLabelSection margin="20px 0 0">
          <InputLabel>
            {t('master_task')} <RedStar>*</RedStar>
          </InputLabel>
          <CustomCheckboxDropdown
            options={masterTaskOptions}
            selectedOptions={selectedMasterTask}
            onChange={onMasterTaskChange}
            placeholder="select_master_tasks"
            countLabel="master_tasks_selected"
            error={saveErrors && saveErrors['master_task']}
            errorMessage={saveErrors && saveErrors['master_task']}
            isLoading={masterTasksLoading}
            disabled={!selectedPublicZone || masterTaskOptions.length === 0}
          />
          {selectedPublicZone &&
            masterTaskOptions.length === 0 &&
            !masterTasksLoading && (
              <FormAlert margin="5px 0 0 0">
                {t('no_master_tasks_available')}
              </FormAlert>
            )}
        </InputWithLabelSection>

        <InputWithLabelSection margin="20px 0 0">
          <InputLabel>
            {t('subtask_name')} <RedStar>*</RedStar>
          </InputLabel>
          <InputField
            type="text"
            placeholder={t('type_subtask_name')}
            value={taskName}
            onChange={e => {
              setTaskName(e.target.value);
              if (saveErrors?.taskName) {
                setSaveErrors({ ...saveErrors, taskName: undefined });
              }
            }}
            style={
              saveErrors?.taskName && {
                border: `1px solid #fe5969`,
                backgroundColor: `rgba(252, 87, 104, 0.05)`,
              }
            }
          />
          {saveErrors?.taskName && <FormAlert>{saveErrors.taskName}</FormAlert>}
        </InputWithLabelSection>

        <InputWithLabelSection margin="20px 0 0">
          <InputLabel>
            {t('office')} <RedStar>*</RedStar>
          </InputLabel>
          <CustomCheckboxDropdown
            options={[
              // Add "All Offices" option at the top
              { label: t('all_offices'), value: 'all' },
              ...officeOptions,
            ]}
            selectedOptions={selectAllOffices ? officeOptions : selectedOffices}
            onChange={onOfficeChange}
            placeholder={t('select_offices')}
            countLabel={t('offices_selected')}
            error={saveErrors && saveErrors['office']}
            errorMessage={saveErrors && saveErrors['office']}
            disabled={!selectedPublicZone}
          />
        </InputWithLabelSection>

        <InputWithLabelSection margin="20px 0 0">
          <InputLabel>
            {t('priority')} <RedStar>*</RedStar>
          </InputLabel>
          <Select
            value={priorityOptions.find(option => option.value === priority)}
            options={priorityOptions}
            onChange={selectedOption => {
              setPriority(selectedOption.value);
              if (saveErrors?.priority) {
                setSaveErrors({ ...saveErrors, priority: undefined });
              }
            }}
            styles={CreateProjectDropdown()}
          />
          {saveErrors?.priority && <FormAlert>{saveErrors.priority}</FormAlert>}
        </InputWithLabelSection>

        <InputWithLabelSection margin="20px 0 0">
          <CommonFlex margin="12px 0 0 0" gap="10px">
            <CheckboxDiv
            active={checkOfficeReport}
            onClick={() => setCheckOfficeReport(prev => !prev)}
            >
              <CheckboxTickIcon src={tickWhite} alt="" />
            </CheckboxDiv>
            <CommonText $label fontWeight="500">
              {t('add_to_office_report')}
            </CommonText>
          </CommonFlex>
        </InputWithLabelSection>

        <CommonGrid
          justifyContent="unset"
          padding="10px 0 0 0"
          margin="100px 0 0"
        >
          <WhiteButton onClick={handleToggle}>{t('cancel')}</WhiteButton>
          <PrimaryButton onClick={handleSubmit}>
            {isLoading ? (
              <ButtonTextLoader
                loadingText={isEditMode ? t('updating') : t('adding')}
                fontSize="13px"
              />
            ) : isEditMode ? (
              t('update_subtask')
            ) : (
              t('add_subtask')
            )}
          </PrimaryButton>
        </CommonGrid>
      </ModalBody>
    </Modal>
  );
};

export default AddMasterSubTaskModal;
